import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Editorial card. Subtle shadow, hair-thin border. Lifts on hover when
 * interactive — `data-interactive` opts in so static cards (e.g. stat
 * callouts) don't float when you mouse over them.
 */
export const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { interactive?: boolean }
>(({ className, interactive, ...props }, ref) => (
  <div
    ref={ref}
    data-interactive={interactive ? "" : undefined}
    className={cn(
      "rounded-lg border border-pb-border bg-pb-surface text-pb-ink shadow-[0_1px_2px_rgb(0_0_0_/_0.02),_0_1px_6px_rgb(0_0_0_/_0.03)]",
      "data-[interactive]:transition-all data-[interactive]:duration-200 data-[interactive]:hover:-translate-y-0.5 data-[interactive]:hover:shadow-[0_1px_2px_rgb(0_0_0_/_0.03),_0_8px_20px_rgb(0_0_0_/_0.06)] data-[interactive]:hover:border-pb-border-strong",
      className,
    )}
    {...props}
  />
));
Card.displayName = "Card";

export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1.5 p-6", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "font-display text-xl font-semibold leading-tight tracking-tight",
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-pb-ink-3", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";
